import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  acceptPostStepProducts,
  getJourneyRunProducts,
  getJourneyRuns,
  getPostStepAttempts,
  getPostSteps,
  redeliverPostStepRetry,
  startPostStepRetry,
  undoPostStepAcceptance,
} from '../services/post-step-retry.service';
import type {
  JourneyProductsQuery,
  JourneyRun,
  JourneyStep,
  PostStepAcceptanceRequest,
  PostStepAcceptanceUndoRequest,
  PostStepName,
  PostStepRetryRequest,
} from '../types/post-step-retry.api.types';

/** Poll cadence while a post-step retry (or its redelivery) is running. */
export const POST_STEP_POLL_MS = 10_000;

/** Page size for the journey products table. */
export const JOURNEY_PRODUCTS_PAGE_SIZE = 50;

/** True while a post-step retry, or the redelivery of one, is still going. */
export function isPostStepLive(runs: JourneyRun[] | null | undefined): boolean {
  return (runs ?? []).some((r) => r.status === 'RUNNING' || r.redelivery?.status === 'RUNNING');
}

/** What changes when a post-step retry or redelivery starts or finishes. */
export function postStepSignature(runs: JourneyRun[] | null | undefined): string | null {
  if (!runs) return null;
  return runs.map((r) => `${r.runId}:${r.status ?? ''}:${r.redelivery?.status ?? ''}`).join(',');
}

/**
 * When a step's journey read changes (a retry or redelivery finished), refetch everything under
 * the batch: job detail and post-steps, every step's journey, the runs and the header. The first
 * read of a step — including switching steps — is not a transition.
 */
export function useInvalidateOnPostStepTransition(
  jobId: string,
  step: JourneyStep,
  runs: JourneyRun[] | null | undefined,
): void {
  const queryClient = useQueryClient();
  const previous = useRef<{ step: JourneyStep; signature: string } | null>(null);
  const signature = postStepSignature(runs);
  useEffect(() => {
    if (signature === null) return;
    const prior = previous.current;
    if (prior !== null && prior.step === step && prior.signature !== signature) {
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    }
    previous.current = { step, signature };
  }, [signature, step, jobId, queryClient]);
}

/** Job-detail `postSteps`; null = absent (older backend). Polls while the last retry runs. */
export function usePostSteps(jobId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.batchPostSteps(jobId),
    queryFn: () => getPostSteps(jobId),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.lastRetry?.status === 'RUNNING' ? POST_STEP_POLL_MS : false,
  });
}

/** The runs that touched one step; null = no journey endpoint (404). Polls while one is running. */
export function useJourneyRuns(jobId: string, step: JourneyStep, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.journeyRuns(jobId, step),
    queryFn: () => getJourneyRuns(jobId, step),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    retry: false,
    refetchInterval: (query) => (isPostStepLive(query.state.data) ? POST_STEP_POLL_MS : false),
  });
}

/** One run's products in a step. */
export function useJourneyRunProducts(
  jobId: string,
  runId: string | null,
  step: JourneyStep,
  query: JourneyProductsQuery,
  live = false,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.journeyRunProducts(jobId, runId ?? '', step, {
      ...query,
    }),
    queryFn: () => getJourneyRunProducts(jobId, runId ?? '', step, query),
    enabled: Boolean(jobId) && Boolean(runId),
    placeholderData: keepPreviousData,
    refetchInterval: live ? POST_STEP_POLL_MS : false,
  });
}

/** One product's post-step attempts. */
export function usePostStepAttempts(jobId: string, productId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.postStepAttempts(jobId, productId ?? ''),
    queryFn: () => getPostStepAttempts(jobId, productId ?? ''),
    enabled: Boolean(jobId) && Boolean(productId),
  });
}

/**
 * One dry run: counts only, nothing starts. The answer carries every scope's count (`scopes`), so
 * the dialog needs no request per radio. A failure (409: the batch is busy) is part of the answer,
 * so it is not retried; callers read `error`.
 */
export function usePostStepDryRun(jobId: string, steps: PostStepName[], enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.postStepDryRun(jobId, steps, 'ALL'),
    queryFn: () => startPostStepRetry(jobId, { steps, scope: 'ALL', dryRun: true }),
    enabled: enabled && Boolean(jobId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Start a post-step retry; the batch page (job detail, journey, runs) refetches. */
export function useStartPostStepRetry(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostStepRetryRequest) => startPostStepRetry(jobId, body),
    onSuccess: (res) => {
      toast.success(`Retry started for ${res.estimatedTargets.toLocaleString()} products`);
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not start the retry'));
    },
  });
}

/** Redeliver what a retry fixed; the server message explains a 409. */
export function useRedeliverPostStepRetry(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (retryId: string) => redeliverPostStepRetry(jobId, retryId),
    onSuccess: () => {
      toast.success('Redelivery started');
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not start the redelivery'));
    },
  });
}

/** Accept products as is; job detail and the journey refetch (counts move out of "to fix"). */
export function useAcceptPostStepProducts(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostStepAcceptanceRequest) => acceptPostStepProducts(jobId, body),
    onSuccess: (res) => {
      if (res.changed === 0) toast.info('Nothing to accept: already fixed or already accepted');
      else
        toast.success(
          res.changed === 1
            ? 'Accepted 1 product as is'
            : `Accepted ${res.changed.toLocaleString()} products as is`,
        );
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not accept the products'));
    },
  });
}

/** Undo "accept as is"; retries pick the products up again. */
export function useUndoPostStepAcceptance(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostStepAcceptanceUndoRequest) => undoPostStepAcceptance(jobId, body),
    onSuccess: () => {
      toast.success('Back on the retry list');
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.catalogOperations.all, 'batches', jobId],
      });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Could not undo'));
    },
  });
}
